import { CAST_PILOT_ASSISTANT } from '@cast/constants';
import { Alert, Icons, Typography, Stack, tokens } from '@cast/design-system';
import { useShouldLimitConsoleForReporting } from '@cast/shared-trial-ui';

import { MAX_INPUT_LENGTH } from './_constants';
import { ChatInput } from './ChatInput';
import { ScrollToBottom } from './ScrollToBottom';

type ChatFooterProps = {
  inputValue: string;
  loadingResponse: boolean;
  showScrollToBottom: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  onInputChange: (value: string) => void;
  onSendMessage: () => void;
  onScrollToBottom: (behavior?: ScrollBehavior) => void;
};

export const ChatFooter = ({
  inputValue,
  loadingResponse,
  showScrollToBottom,
  textareaRef,
  onInputChange,
  onSendMessage,
  onScrollToBottom,
}: ChatFooterProps) => {
  const limitReportingConsole = useShouldLimitConsoleForReporting();

  return (
    <Stack
      sx={{
        px: '24px',
        pb: '8px',
        position: 'relative',
        backgroundColor: 'transparent',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -40,
          left: '24px',
          right: '24px',
          height: '40px',
          background: `linear-gradient(to top, ${tokens.theme.bg.structural.surface} 0%, transparent 100%)`,
          pointerEvents: 'none',
        },
      }}
    >
      {showScrollToBottom && <ScrollToBottom onClick={onScrollToBottom} />}
      <Stack gap={8}>
        {limitReportingConsole ? (
          <Alert
            color="info"
            size="sm"
            icon={<Icons.Lock weight="fill" />}
            title="Ask your own question"
            testId="castpilot-assistant-custom-questions-locked"
          >
            Custom questions are available with a paid plan. You can still use
            suggested questions and {CAST_PILOT_ASSISTANT} actions throughout
            Cast AI.
          </Alert>
        ) : (
          <ChatInput
            ref={textareaRef}
            value={inputValue}
            onChange={onInputChange}
            onSendMessage={onSendMessage}
            loadingResponse={loadingResponse}
            maxLength={MAX_INPUT_LENGTH}
          />
        )}
        <Typography
          variant="P10R"
          color={tokens.theme.fg.structural.secondary}
          textAlign="center"
        >
          AI can make mistakes. Verify important information.
        </Typography>
      </Stack>
    </Stack>
  );
};
